
import React, { useEffect } from 'react';
import { ResourceFile } from "../../types";
import { TrainingQuizContentViewer } from "../TrainingQuizContentViewer";
import { useQuizData } from "@/components/resource/useQuizData";

interface QuizContentTypeHandlerProps {
  currentFile: ResourceFile;
  currentFileIndex: number;
  files: ResourceFile[];
  completedFiles: {[key: string]: boolean};
  isAdmin: boolean;
  isTrainee: boolean;
  isFileLocked: (index: number) => boolean;
  overallProgress: number;
  resourceId: string;
  quiz: any;
  isLoadingQuiz: boolean;
  quizCompleted: boolean;
  quizAttempt: any;
  handleQuizComplete: (quizId: string, attempt: any) => Promise<void>;
  onComplete?: () => void;
  error?: string | null;
}

export function QuizContentTypeHandler({
  currentFile,
  currentFileIndex,
  files,
  completedFiles,
  isAdmin,
  isTrainee,
  isFileLocked,
  overallProgress,
  resourceId,
  quiz,
  isLoadingQuiz,
  quizCompleted,
  quizAttempt,
  handleQuizComplete,
  onComplete,
  error
}: QuizContentTypeHandlerProps) {
  const isLastQuizOrSection = currentFileIndex === files.length - 1 || 
    files.slice(currentFileIndex + 1).every(file => 
      completedFiles[file.id] || isQuizFile(file)
    );

  // Use the resource's useQuizData hook directly to ensure fresh data
  const {
    quiz: fileQuiz,
    isLoadingQuiz: isLoading,
    error: quizError,
    quizCompleted: localQuizCompleted,
    quizAttempt: localQuizAttempt,
    fetchQuiz
  } = useQuizData(resourceId);

  // Fetch quiz data when component mounts or when resource/file changes
  useEffect(() => {
    // Always make sure we have a valid quiz_id before fetching
    if (resourceId && currentFile) {
      console.log('QuizContentTypeHandler: Fetching quiz for file with quiz_id:', currentFile.quiz_id);
      fetchQuiz();
    }
  }, [resourceId, currentFile, fetchQuiz]);

  // Determine which quiz data to use
  const quizToUse = fileQuiz || quiz;
  const loadingStatus = isLoading || isLoadingQuiz;
  const errorMessage = quizError || error;
  
  // Use local quiz completion data or the data passed in from parent
  const isQuizCompleted = localQuizCompleted || quizCompleted;
  const attemptToUse = localQuizAttempt || quizAttempt;

  // Debug logging to help identify issues
  console.log('QuizContentTypeHandler details:', {
    fileId: currentFile.id,
    fileTitle: currentFile.title,
    fileQuizId: currentFile.quiz_id,
    hasQuiz: Boolean(quizToUse),
    quizData: quizToUse,
    isLoading: loadingStatus,
    isQuizCompleted,
    hasAttempt: !!attemptToUse,
    error: errorMessage
  });

  return (
    <TrainingQuizContentViewer
      quiz={quizToUse}
      isLoadingQuiz={loadingStatus}
      isAccessible={isAdmin || !isTrainee || !isFileLocked(currentFileIndex)}
      progress={overallProgress}
      isLastSection={isLastQuizOrSection}
      quizId={currentFile.quiz_id || quizToUse?.id}
      resourceId={resourceId}
      quizCompleted={isQuizCompleted}
      quizAttempt={attemptToUse}
      error={errorMessage}
      onComplete={() => {
        if (currentFileIndex < files.length - 1) {
          // This will be handled in the parent component
          if (onComplete) onComplete();
        } else if (onComplete) {
          onComplete();
        }
      }}
      onQuizUpdate={(quizId, attempt) => handleQuizComplete(quizId, attempt)}
    />
  );
}

// Utility function
function isQuizFile(file: ResourceFile): boolean {
  return file.file_type === 'quiz' || file.resourceType === 'quiz';
}
